import type {
  MailCreateFolderMutationPayload,
  MailDeleteFolderMutationPayload,
  MailMoveMessagesMutationPayload,
  MailSendMutationPayload,
  MailUpdateFolderMutationPayload,
  MailUpdateMessageStateMutationPayload,
  OfflineMutationRecord,
} from '../../offline-mutation-outbox';
import type { MailMailboxFolderInfo, MailMessageInfo } from './types';

export type MailMutationSyncState = 'failed' | 'pending';

export type MailMutationProjection = {
  accountId: string | null;
  count: number;
  folderId: string | null;
  id: string;
  itemLabel: string;
  messageIds: string[];
  operation: OfflineMutationRecord['operation'];
  syncState: MailMutationSyncState;
  targetFolderId: string | null;
};

export function projectMailMutationRecords(input: {
  failed: readonly OfflineMutationRecord[];
  pending: readonly OfflineMutationRecord[];
  workspaceId: string | null;
}): MailMutationProjection[] {
  if (!input.workspaceId) return [];
  return [
    ...input.pending.map(record => ({ record, syncState: 'pending' as const })),
    ...input.failed.map(record => ({ record, syncState: 'failed' as const })),
  ]
    .filter(
      ({ record }) =>
        record.workspaceId === input.workspaceId &&
        record.operation.startsWith('mail.')
    )
    .sort(
      (left, right) =>
        left.record.createdAt - right.record.createdAt ||
        (left.record.sequence ?? 0) - (right.record.sequence ?? 0) ||
        left.record.id.localeCompare(right.record.id)
    )
    .map(({ record, syncState }) =>
      toMailMutationProjection(record, syncState)
    );
}

export function latestMailFolderMutationStates(
  projections: readonly MailMutationProjection[]
): Map<string, MailMutationProjection> {
  const states = new Map<string, MailMutationProjection>();
  for (const projection of projections) {
    if (projection.folderId) states.set(projection.folderId, projection);
  }
  return states;
}

export function latestMailMessageMutationStates(
  projections: readonly MailMutationProjection[]
): Map<string, MailMutationProjection> {
  const states = new Map<string, MailMutationProjection>();
  for (const projection of projections) {
    for (const messageId of projection.messageIds) {
      states.set(messageId, projection);
    }
  }
  return states;
}

export function applyMailFolderMutationProjection(
  folders: readonly MailMailboxFolderInfo[] | undefined,
  projections: readonly MailMutationProjection[],
  records: readonly OfflineMutationRecord[]
): MailMailboxFolderInfo[] | undefined {
  if (!folders) return undefined;
  const recordById = new Map(records.map(record => [record.id, record]));
  const updates = new Map<string, MailUpdateFolderMutationPayload['input']>();
  for (const projection of projections) {
    if (projection.operation !== 'mail.updateFolder' || !projection.folderId) {
      continue;
    }
    const record = recordById.get(projection.id);
    if (!record) continue;
    const payload = record.payload as MailUpdateFolderMutationPayload;
    updates.set(projection.folderId, payload.input);
  }
  if (updates.size === 0) return [...folders];
  return folders.map(folder => {
    const update = updates.get(folder.id);
    const displayName = update?.displayName?.trim();
    if (!displayName) return folder;
    const delimiter = folder.delimiter || '/';
    const pathParts = folder.path.split(delimiter);
    pathParts[pathParts.length - 1] = displayName;
    return { ...folder, displayName, path: pathParts.join(delimiter) };
  });
}

export function applyMailMessageMutationProjection(
  messages: readonly MailMessageInfo[] | undefined,
  folders: readonly MailMailboxFolderInfo[] | undefined,
  projections: readonly MailMutationProjection[],
  records: readonly OfflineMutationRecord[]
): MailMessageInfo[] | undefined {
  if (!messages) return undefined;
  const recordById = new Map(records.map(record => [record.id, record]));
  const folderById = new Map(
    (folders ?? []).map(folder => [folder.id, folder])
  );
  let next = [...messages];
  for (const projection of projections) {
    const record = recordById.get(projection.id);
    if (!record) continue;
    if (projection.operation === 'mail.updateMessageState') {
      const { input } = record.payload as MailUpdateMessageStateMutationPayload;
      next = next.map(message =>
        message.id !== input.messageId
          ? message
          : {
              ...message,
              ...(typeof input.isRead === 'boolean'
                ? { isRead: input.isRead }
                : {}),
              ...(typeof input.isFlagged === 'boolean'
                ? { isFlagged: input.isFlagged }
                : {}),
              ...(typeof input.isStarred === 'boolean'
                ? { isStarred: input.isStarred }
                : {}),
            }
      );
      continue;
    }
    if (
      projection.operation === 'mail.moveMessages' &&
      projection.syncState === 'pending'
    ) {
      const payload = record.payload as MailMoveMessagesMutationPayload;
      const messageIds = new Set(payload.messageIds);
      const targetFolder = folderById.get(payload.folderId);
      const targetFolderRole = targetFolder?.role as
        | MailMessageInfo['folder']
        | undefined;
      next = next.map(message =>
        !messageIds.has(message.id)
          ? message
          : {
              ...message,
              folderId: payload.folderId,
              ...(targetFolderRole ? { folder: targetFolderRole } : {}),
            }
      );
    }
  }
  return next;
}

function toMailMutationProjection(
  record: OfflineMutationRecord,
  syncState: MailMutationSyncState
): MailMutationProjection {
  switch (record.operation) {
    case 'mail.createFolder': {
      const { input } = record.payload as MailCreateFolderMutationPayload;
      return baseProjection(record, syncState, {
        accountId: input.accountId,
        itemLabel: input.displayName,
      });
    }
    case 'mail.updateFolder': {
      const { input } = record.payload as MailUpdateFolderMutationPayload;
      return baseProjection(record, syncState, {
        folderId: input.folderId,
        itemLabel: input.displayName?.trim() || input.folderId,
      });
    }
    case 'mail.deleteFolder': {
      const payload = record.payload as MailDeleteFolderMutationPayload;
      return baseProjection(record, syncState, {
        folderId: payload.folderId,
        itemLabel: payload.folderId,
      });
    }
    case 'mail.moveMessages': {
      const payload = record.payload as MailMoveMessagesMutationPayload;
      return baseProjection(record, syncState, {
        count: Math.max(1, payload.messageIds.length),
        messageIds: payload.messageIds,
        targetFolderId: payload.folderId,
      });
    }
    case 'mail.updateMessageState': {
      const { input } = record.payload as MailUpdateMessageStateMutationPayload;
      return baseProjection(record, syncState, {
        messageIds: [input.messageId],
      });
    }
    case 'mail.send': {
      const { input } = record.payload as MailSendMutationPayload;
      return baseProjection(record, syncState, {
        accountId: input.accountId,
        itemLabel: input.subject?.trim() || '',
      });
    }
    default:
      return baseProjection(record, syncState);
  }
}

function baseProjection(
  record: OfflineMutationRecord,
  syncState: MailMutationSyncState,
  patch: Partial<MailMutationProjection> = {}
): MailMutationProjection {
  return {
    accountId: null,
    count: 1,
    folderId: null,
    id: record.id,
    itemLabel: '',
    messageIds: [],
    operation: record.operation,
    syncState,
    targetFolderId: null,
    ...patch,
  };
}
